import { useState, useEffect } from "react";
import { API , authFetch, useEditMode, useAuth } from "@/App";
import { toast } from "sonner";
import { mutate } from "@/lib/apiMutate";
import { RegionSwitcher } from "@/components/RegionSwitcher";
import { useRegionFilter } from "@/hooks/useRegionFilter";
import { ClipboardList, Plus, Search, MoreHorizontal, Check, Clock, AlertCircle, Camera, Image, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { TaskDetailDialog } from "@/components/TaskDetailDialog";

const TasksPage = () => {
  const canEdit = useEditMode();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const { activeRegion, setActiveRegion, filterByRegion } = useRegionFilter();
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    assigned_to: "",
    due_date: "",
    priority: "MEDIUM",
    task_type: "DEALER_VISIT",
  });
  const [taskPhotoFiles, setTaskPhotoFiles] = useState([]);
  const [detailOpen, setDetailOpen] = useState(null); // task object

  useEffect(() => {
    fetchData();
  }, [statusFilter, priorityFilter]);

  const fetchData = async () => {
    try {
      let url = `${API}/tasks?`;
      if (statusFilter && statusFilter !== "all") url += `status=${statusFilter}&`;
      if (priorityFilter && priorityFilter !== "all") url += `priority=${priorityFilter}&`;

      const [tasksRes, employeesRes] = await Promise.all([
        authFetch(url, {  }),
        authFetch(`${API}/employees?status=ACTIVE`, {  }),
      ]);

      const tasksData = await tasksRes.json();
      const employeesData = await employeesRes.json();

      if (tasksData.success) setTasks(tasksData.data);
      if (employeesData.success) setEmployees(employeesData.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let attachments = [];
    if (taskPhotoFiles.length > 0) {
      try {
        const fd = new FormData();
        taskPhotoFiles.forEach((f) => fd.append("files", f));
        const uploadRes = await authFetch(`${API}/upload-multiple?category=tasks`, { method: "POST", body: fd });
        const uploadData = await uploadRes.json();
        if (uploadData.success) attachments = uploadData.data.map((d) => d.storage_path);
      } catch {
        toast.error("Photo upload failed; continuing without photos");
      }
    }
    const r = await mutate(
      authFetch,
      `${API}/tasks`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, attachments }),
      },
      { successMessage: "Task created", failureMessage: "Failed to create task" },
    );
    if (!r.ok) return;
    setIsDialogOpen(false);
    resetForm();
    setTaskPhotoFiles([]);
    fetchData();
  };

  const handleStatusUpdate = async (taskId, status) => {
    const r = await mutate(
      authFetch,
      `${API}/tasks/${taskId}/status?status=${status}`,
      { method: "PATCH" },
      { successMessage: "Task updated", failureMessage: "Failed to update task" },
    );
    if (r.ok) fetchData();
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      assigned_to: "",
      due_date: "",
      priority: "MEDIUM",
      task_type: "DEALER_VISIT",
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      OPEN: { class: "badge-info", icon: Clock },
      IN_PROGRESS: { class: "badge-warning", icon: Clock },
      COMPLETED: { class: "badge-success", icon: Check },
      OVERDUE: { class: "badge-danger", icon: AlertCircle },
    };
    const config = styles[status] || styles.OPEN;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.class}`}>
        <Icon className="w-3 h-3" />
        {status}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      HIGH: "bg-red-100 text-red-700 border-red-200",
      MEDIUM: "bg-amber-100 text-amber-700 border-amber-200",
      LOW: "bg-slate-100 text-slate-700 border-slate-200",
    };
    return (
      <Badge variant="outline" className={styles[priority] || "bg-slate-100 text-slate-700"}>
        {priority}
      </Badge>
    );
  };

  // Region-filtered slice (tasks may have region_id, fall back to assigned employee)
  const employeeById = employees.reduce((acc, e) => {
    if (e?.employee_id) acc[e.employee_id] = e;
    return acc;
  }, {});
  const regionTasks = filterByRegion(tasks, (t) =>
    t.region_id || employeeById[t.assigned_to]?.region_id
  );

  // Calculate stats (region-aware)
  const openTasks = regionTasks.filter((t) => t.status === "OPEN" || t.status === "IN_PROGRESS").length;
  const completedTasks = regionTasks.filter((t) => t.status === "COMPLETED").length;
  const overdueTasks = regionTasks.filter((t) => t.status === "OVERDUE").length;

  return (
    <div className="space-y-6" data-testid="tasks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Sora']">Tasks</h1>
          <p className="text-slate-500 text-sm mt-1">Manage field team tasks</p>
        </div>
        <div className="flex items-center gap-2">
          <RegionSwitcher
            active={activeRegion}
            onChange={setActiveRegion}
            isSuperAdmin={isSuperAdmin}
            dataTestId="tasks-region-switcher"
          />
        {canEdit && (
          <Button
            onClick={() => {
              resetForm();
              setIsDialogOpen(true);
            }}
            className="btn-primary"
            data-testid="add-task-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Task
          </Button>
        )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <p className="text-sm text-slate-500">Open Tasks</p>
          <p className="text-2xl font-bold text-blue-600 font-mono mt-1">{openTasks}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-slate-500">Completed</p>
          <p className="text-2xl font-bold text-emerald-600 font-mono mt-1">{completedTasks}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-slate-500">Overdue</p>
          <p className="text-2xl font-bold text-red-600 font-mono mt-1">{overdueTasks}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-slate-500">Total</p>
          <p className="text-2xl font-bold text-slate-900 font-mono mt-1">{tasks.length}</p>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="OPEN">Open</SelectItem>
              <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
              <SelectItem value="COMPLETED">Completed</SelectItem>
              <SelectItem value="OVERDUE">Overdue</SelectItem>
            </SelectContent>
          </Select>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="All Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priority</SelectItem>
              <SelectItem value="HIGH">High</SelectItem>
              <SelectItem value="MEDIUM">Medium</SelectItem>
              <SelectItem value="LOW">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Tasks Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="tasks-table">
            <thead>
              <tr>
                <th>Task</th>
                <th>Assigned To</th>
                <th>Type</th>
                <th>Due Date</th>
                <th>Priority</th>
                <th>Status</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-8">
                    <div className="w-6 h-6 spinner mx-auto"></div>
                  </td>
                </tr>
              ) : regionTasks.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8">
                    <div className="empty-state">
                      <ClipboardList className="empty-state-icon" />
                      <p className="empty-state-title">No tasks found</p>
                      <p className="empty-state-description">
                        Create tasks for your field team.
                      </p>
                      <Button onClick={() => setIsDialogOpen(true)} className="btn-primary">
                        <Plus className="w-4 h-4 mr-2" />
                        Create Task
                      </Button>
                    </div>
                  </td>
                </tr>
              ) : (
                regionTasks.map((task) => {
                  const employee = employees.find((e) => e.employee_id === task.assigned_to);
                  const attachCount = (task.attachments || task.photos || []).length;
                  return (
                    <tr key={task.task_id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                      <td>
                        <button
                          type="button"
                          onClick={() => setDetailOpen(task)}
                          className="text-left"
                          data-testid={`open-task-${task.task_id}`}
                        >
                          <div>
                            <p className="font-medium text-slate-900 hover:text-[#8B1A1A] hover:underline flex items-center gap-1.5">
                              {task.title}
                              {attachCount > 0 && (
                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-violet-50 text-violet-700 border border-violet-200" title={`${attachCount} attachment${attachCount > 1 ? "s" : ""}`}>
                                  <Image className="w-2.5 h-2.5" /> {attachCount}
                                </span>
                              )}
                            </p>
                            {task.description && (
                              <p className="text-xs text-slate-500 truncate max-w-xs">
                                {task.description}
                              </p>
                            )}
                          </div>
                        </button>
                      </td>
                      <td>{employee?.full_name || "-"}</td>
                      <td>
                        <span className="text-xs text-slate-500">{task.task_type}</span>
                      </td>
                      <td>{task.due_date}</td>
                      <td>{getPriorityBadge(task.priority)}</td>
                      <td>{getStatusBadge(task.status)}</td>
                      <td>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => setDetailOpen(task)}
                              data-testid={`view-task-${task.task_id}`}
                            >
                              <ClipboardList className="w-4 h-4 mr-2" />
                              View Timeline
                            </DropdownMenuItem>
                            {task.status === "OPEN" && (
                              <DropdownMenuItem
                                onClick={() => handleStatusUpdate(task.task_id, "IN_PROGRESS")}
                              >
                                <Clock className="w-4 h-4 mr-2" />
                                Start Task
                              </DropdownMenuItem>
                            )}
                            {task.status !== "COMPLETED" && (
                              <DropdownMenuItem
                                onClick={() => handleStatusUpdate(task.task_id, "COMPLETED")}
                                className="text-emerald-600"
                              >
                                <Check className="w-4 h-4 mr-2" />
                                Mark Complete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Create Task Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md" data-testid="task-dialog">
          <DialogHeader>
            <DialogTitle>Create Task</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Title</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Task title"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Task description..."
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Assign To</label>
                <Select
                  value={formData.assigned_to}
                  onValueChange={(value) => setFormData({ ...formData, assigned_to: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.employee_id} value={emp.employee_id}>
                        {emp.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Due Date</label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Priority</label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) => setFormData({ ...formData, priority: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="HIGH">High</SelectItem>
                    <SelectItem value="MEDIUM">Medium</SelectItem>
                    <SelectItem value="LOW">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Task Type</label>
                <Select
                  value={formData.task_type}
                  onValueChange={(value) => setFormData({ ...formData, task_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DEALER_VISIT">Dealer Visit</SelectItem>
                    <SelectItem value="COLLECTION_FOLLOWUP">Collection Follow-up</SelectItem>
                    <SelectItem value="COMPLAINT_RESOLUTION">Complaint Resolution</SelectItem>
                    <SelectItem value="MARKET_SURVEY">Market Survey</SelectItem>
                    <SelectItem value="SCHEME_COMMUNICATION">Scheme Communication</SelectItem>
                    <SelectItem value="OTHER">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Photo/Attachment Upload */}
            <div>
              <label className="text-sm font-medium text-slate-700">Attachments (optional)</label>
              <label className="flex items-center gap-2 mt-1 px-3 py-2.5 border border-dashed border-slate-300 rounded-md cursor-pointer hover:border-[#8B1A1A] bg-slate-50 transition-colors" data-testid="task-photo-upload">
                <Camera className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-500">{taskPhotoFiles.length > 0 ? `${taskPhotoFiles.length} file(s) selected` : "Click to upload photos"}</span>
                <input type="file" className="hidden" accept="image/*,.pdf" multiple onChange={(e) => setTaskPhotoFiles(Array.from(e.target.files || []))} />
              </label>
              {taskPhotoFiles.length > 0 && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  {taskPhotoFiles.map((f, i) => (
                    <div key={i} className="flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-xs text-slate-600">
                      <Image className="w-3 h-3" /> {f.name}
                      <button type="button" onClick={() => setTaskPhotoFiles(taskPhotoFiles.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-600 ml-1"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary">
                Create Task
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {detailOpen && (
        <TaskDetailDialog
          open={!!detailOpen}
          onClose={() => setDetailOpen(null)}
          task={detailOpen}
          employee={employees.find((e) => e.employee_id === detailOpen.assigned_to)}
        />
      )}
    </div>
  );
};

export default TasksPage;
